/// <reference lib="webworker" />

export interface TaggedMessage<TType extends string> {
  type: TType;
}

/**
 * The small part of a worker scope that this helper needs. Tests pass a fake
 * scope here; real workers pass `self`.
 */
export interface WebWorkerScope {
  onmessage: ((event: MessageEvent) => void) | null;
  postMessage: (message: unknown) => void;
}

/**
 * Base class for app Web Workers.
 *
 * Each worker defines a request type and a result type. Messages that do not use
 * those types are ignored. This keeps third-party worker messages from being
 * treated as app data.
 */
export abstract class TypedWebWorker<
  TRequest extends TaggedMessage<string>,
  TResult extends TaggedMessage<string>,
> {
  protected abstract readonly requestType: TRequest['type'];
  protected abstract readonly resultType: TResult['type'];

  /** Run after a request has passed the type check. */
  protected abstract handle(request: TRequest): TResult;

  /** Check whether a value is this worker's request message. */
  getIsRequest(value: unknown): value is TRequest {
    return hasMessageType(value, this.requestType);
  }

  /** Check whether a value is this worker's result message. */
  getIsResult(value: unknown): value is TResult {
    return hasMessageType(value, this.resultType);
  }

  /**
   * Worker side: listen for requests. Messages from tools or other workers are
   * ignored unless they match this worker's request type.
   */
  listen(scope: WebWorkerScope): void {
    scope.onmessage = (event: MessageEvent) => {
      if (!this.getIsRequest(event.data)) {
        return;
      }
      scope.postMessage(this.handle(event.data));
    };
  }

  /**
   * Worker side: call `listen(self)`, but only inside a Web Worker. When a
   * client imports the worker module on the main thread, this does nothing.
   */
  listenIfWorker(): void {
    if (
      typeof WorkerGlobalScope === 'undefined' ||
      typeof self === 'undefined' ||
      !(self instanceof WorkerGlobalScope)
    ) {
      return;
    }
    this.listen(self as unknown as WebWorkerScope);
  }

  /**
   * Main thread: send one request to a new worker. The first matching result
   * resolves the promise; other messages are ignored.
   */
  run(createWorker: () => Worker, request: TRequest): Promise<TResult> {
    return new Promise<TResult>((resolve, reject) => {
      const worker = createWorker();

      worker.onmessage = (event: MessageEvent) => {
        if (!this.getIsResult(event.data)) {
          return;
        }
        resolve(event.data);
        worker.terminate();
      };

      worker.onerror = event => {
        reject(event.error ?? new Error(event.message || 'Worker error'));
        worker.terminate();
      };

      worker.postMessage(request);
    });
  }
}

function hasMessageType(value: unknown, type: string): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as {type?: unknown}).type === type
  );
}
